import * as React from 'react';
import { useDispatch } from 'react-redux';
import { getDocumentHelper } from '../../../../actions/utility-actions';
import { IsolatedFilterBar } from '../../../../components/filter-bar/IsolatedFilterBar';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { AppliedFilterBarOpt } from '../../../../models/utilities/filter-bar';
import { StatusCode } from '../../../../utils/api';
import { PageNames } from '../../../../utils/app-constants';
import { CreateRunForOrderIdsFlyout } from '../components/CreateRunForOrderIdsFlyout';
import { CreateRunForPickupFlyout } from '../../../../production/components/CreateRunForPickupFlyout';
import { MetricTable } from '../../../../production/components/MetricTable';
import { MetricTotals } from '../../../../production/components/MetricTotals';
import { MetricsDetailFlyout } from '../../../../production/components/MetricsDetailFlyout';
import { OrderGroupingFlyout } from '../../../../production/components/OrderGroupingFlyout';
import { PlanningType } from '../../../../production/models/enums/planning-type';
import { MetricsSearchFilters } from '../../../../production/models/metrics-search-filters';
import { MetricsPickupExport } from '../../../../production/models/metrics/metrics-pickup-export-document';
import { UnplannedPickupMetricsGroup } from '../../../../production/models/metrics/unplanned-pickup-metrics-group';
import { UnplannedPickupOrder } from '../../../../production/models/metrics/unplanned-pickup-order';
import { CellMetric } from '../../../../production/models/shared/metric-base';
import { MetricRunningTotal } from '../../../../production/models/shared/metric-running-total';
import { ProductionSvc } from '../../../../production/service';

interface IProps extends IBaseSceneProps {
    exportFlag: number;
    createFromOrderIdsFlag: number;
    setButtonState: any;
}

export const ProductionPlanningOrdersScene = (props: IProps) => {

    const dispatch = useDispatch();
    const [tasks, setTasks] = React.useState<UnplannedPickupMetricsGroup[]>([]);
    const [service] = React.useState(new ProductionSvc());
    const [isCreateOpen, setIsCreateOpen] = React.useState(false);
    const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
    const [isGroupingOpen, setIsGroupingOpen] = React.useState(false);
    const [isCreateFromOrderIdsOpen, setIsCreateFromOrderIdsOpen] = React.useState(false);
    const [selecteds, setSelecteds] = React.useState<UnplannedPickupMetricsGroup[]>([]);
    const [selectedTotal, setSelectedTotal] = React.useState<MetricRunningTotal>(new MetricRunningTotal());
    const [cellData, setCellData] = React.useState<any[]>([]);
    const [showRowTotal, setShowRowTotal] = React.useState<boolean>(false);
    const [filterParams, setFilterParams] = React.useState<MetricsSearchFilters>(new MetricsSearchFilters());
    const [filterDate, setFilterDate] = React.useState<string | undefined>(undefined);
    const [filterDivision, setFilterDivision] = React.useState<string | undefined>(undefined);
    const [filterShipmentMethod, setFilterShipmentMethod] = React.useState<string | undefined>(undefined);

    const [orders, setOrders] = React.useState<UnplannedPickupOrder[]>([]);
    const [flyoutTitle, setFlyoutTitle] = React.useState<string>('');

    const parseCells = (data: any[]) => {
        const celltable: any[] = [];
        data.forEach((itm: UnplannedPickupMetricsGroup) => {
            celltable.push([...itm.METRIC_LIST]);
        });

        setCellData(celltable);
    };

    async function getItems(f: MetricsSearchFilters) {
        setTasks([]);
        setCellData([]);
        setSelecteds([]);
        setSelectedTotal(new MetricRunningTotal());
        const tms = await service.getUnplannedPickupTasks(filterParams, false);
        if (tms.status === StatusCode.Success) {
            setTasks([...tms.data]);
            parseCells(tms.data);
        }
    }

    React.useEffect(() => {
        if (props.exportFlag > 0) {

            const req = new MetricsPickupExport(filterParams);
            dispatch(getDocumentHelper(req));

        }
    }, [props.exportFlag]);

    // The button is in the heading of the page, which keeps its count when this tab is left and come back to, so the
    // flyout opens when the count changes and not when the tab is shown with a count that was already used
    const createFromOrderIdsCount = React.useRef(props.createFromOrderIdsFlag);
    React.useEffect(() => {
        if (props.createFromOrderIdsFlag !== createFromOrderIdsCount.current) {
            createFromOrderIdsCount.current = props.createFromOrderIdsFlag;
            setIsCreateFromOrderIdsOpen(true);
        }
    }, [props.createFromOrderIdsFlag]);

    /**
     * Close the flyout that creates a run from order IDs, and fetch the orders again when a run was created
     */
    const closeCreateFromOrderIdsFlyout = async (created: boolean) => {
        setIsCreateFromOrderIdsOpen(false);
        if (created) {
            await getItems(filterParams);
        }
    };

    /**
     * Close the create flyout
     */
    const closeCreateFlyout = async (refresh: boolean) => {
        setIsCreateOpen(false);
        if (refresh) {
            await getItems(filterParams);
        }
    };

    /**
     * Close the grouping flyout, and fetch the groups again when they were changed
     */
    const closeGroupingFlyout = async (saved: boolean) => {
        setIsGroupingOpen(false);
        if (saved) {
            await getItems(filterParams);
        }
    };

    /**
     * Close the detail flyout
     */
    const closeDetailFlyout = async () => {
        setIsDetailsOpen(false);
    };

    const openDetailFlyout = async () => {

        if (!selecteds || selecteds.length > 1) {
            return;
        }

        const group = selecteds[0] as UnplannedPickupMetricsGroup;

        const results = await service.getUnplannedPickupOrders(filterParams, group, false);
        if (results.status === StatusCode.Success) {
            setOrders(results.data);

            const title = `${group.name} [${group.dealerId}]`;
            setFlyoutTitle(title);

            setIsDetailsOpen(true);
        }
    };

    const onCellChange = (cells: any[], fullRows: any[]) => {
        setCellData(cells);

        const totalV2 = new MetricRunningTotal();
        cells.forEach((itm: CellMetric[]) => {

            itm.forEach((citm: CellMetric, cellIndx: number) => {

                if (cellIndx < 1) {
                    return;
                }

                if (citm.isCurrentlySelected === true && citm.jobType !== undefined && citm.jobType >= 0) {
                    for (let i = 0; i < totalV2.METRIC_LIST.length; i++) {
                        if (totalV2.METRIC_LIST[i].jobType === citm.jobType) {
                            totalV2.METRIC_LIST[i].quantity += citm.quantity;
                            break;
                        }
                    }
                }
            });
        });

        if (fullRows.length > 0) {
            setShowRowTotal(true);

            const dealers: any[] = [];
            fullRows.forEach((itm: any) => (
                dealers.push(tasks[itm.index])
            ));

            setSelecteds(dealers);
        }
        else {
            setShowRowTotal(false);
        }

        setSelectedTotal(totalV2);

    };

    const onFilterReset = () => {
        setFilterParams(new MetricsSearchFilters());
    };

    const onParamsUpdate = (itm: any) => {
        setFilterParams(itm);
    };

    /**
     * OnSearch handler
     * @param filters The list of filters to apply to the search
     */
    const onSearch = async (filters: MetricsSearchFilters) => {

        const response = await getItems(filters);
        const cutoffFilter = filters.appliedFilters.find((fItm: AppliedFilterBarOpt) => {
            return fItm.qsName === 'maxCutoff';
        });

        // What was chosen of a filter that has several to choose from: all of them, not the first
        const chosen = (qsName: string) => {
            const applied = filters.appliedFilters.filter((fItm: AppliedFilterBarOpt) => fItm.qsName === qsName);
            return applied.length > 0 ? applied.map((fItm: AppliedFilterBarOpt) => fItm.qsValue).join(',') : undefined;
        };

        if (cutoffFilter) {
            setFilterDate(cutoffFilter.qsValue);
        }
        else {
            setFilterDate(undefined);
        }

        setFilterDivision(chosen('division'));

        setFilterShipmentMethod(chosen('shipmentMethod'));

        setFilterParams(filters);
    };

    return (<PageContent classes="production-module" pageName={PageNames.PRODUCTION_PLANNING_ORDERS}>
        <PageBody>

            <IsolatedFilterBar
                filterProps={filterParams}
                searchCallback={onSearch}
                onReset={onFilterReset}
                onFilterUpdate={onParamsUpdate}
            />

            <MetricTable
                runs={tasks}
                cells={cellData}
                onCellChange={onCellChange}
                onlyRowSelect={true}
                showLegend={false}
                onViewDetails={openDetailFlyout}
                onStartProduction={() => setIsCreateOpen(true)}
                onSetGrouping={() => setIsGroupingOpen(true)}
                headers={['Dealer ID', 'Name', 'Method']}
                nonMetricPropCount={3}
            />

            <MetricTotals
                totals={selectedTotal}
                rowSelected={showRowTotal}
                shouldShow={true}
            />

            <CreateRunForPickupFlyout
                taskGroups={selecteds}
                isOpen={isCreateOpen}
                onClose={closeCreateFlyout}
                isUrgent={false}
                maxCutoff={filterDate}
                division={filterDivision}
                shipmentMethod={filterShipmentMethod}
            />

            <CreateRunForOrderIdsFlyout
                isOpen={isCreateFromOrderIdsOpen}
                onClose={closeCreateFromOrderIdsFlyout}
            />

            <OrderGroupingFlyout
                isOpen={isGroupingOpen}
                group={selecteds.length === 1 ? selecteds[0] : undefined}
                filters={filterParams}
                onClose={closeGroupingFlyout}
            />

            <MetricsDetailFlyout
                orders={orders}
                title={flyoutTitle}
                type={PlanningType.Pickup}
                isOpen={isDetailsOpen}
                onClose={closeDetailFlyout}
            />

        </PageBody>
    </PageContent>);

};
